import { Icon } from './Icons';
import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip } from 'chart.js';
import { loadAllBloodMarkers, loadLatestMetrics, loadMetricHistory } from '../db';
import { collection, getDocs, query, orderBy, limit, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { BIOMARKER_GROUPS, getStatus } from '../biomarkers';
import { auth } from '../firebase';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip);

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function calcBMI(h, w) {
  if (!h || !w) return null;
  return (parseFloat(w) / Math.pow(parseFloat(h) / 100, 2)).toFixed(1);
}
function calcAge(dob) {
  if (!dob) return null;
  return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
}
function getHour() { return new Date().getHours(); }
function getGreeting() {
  const h = getHour();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const STATUS_COLORS = { ok: 'var(--success)', warn: 'var(--warning)', bad: 'var(--danger)', none: 'var(--text3)' };
const STATUS_DIM = { ok: 'var(--success-dim)', warn: 'var(--warning-dim)', bad: 'var(--danger-dim)', none: 'var(--bg-elevated)' };
const STATUS_LABELS = { ok: 'Optimal', warn: 'Borderline', bad: 'Elevated', none: '—' };

// ── Health Score Calculator ─────────────────────────────────────────────────
function calcHealthScore(bloodData, body, mental, fitness) {
  let score = 100;
  let penalties = 0;
  let checks = 0;

  const check = (val, statusFn, weight) => {
    if (!val) return;
    checks++;
    const s = statusFn();
    if (s === 'warn') penalties += weight * 0.5;
    if (s === 'bad') penalties += weight;
  };

  check(bloodData.fastingGlucose, () => getStatus('glucose', 'fastingGlucose', bloodData.fastingGlucose), 15);
  check(bloodData.totalCholesterol, () => getStatus('lipids', 'totalCholesterol', bloodData.totalCholesterol), 12);
  check(bloodData.systolicBP, () => getStatus('cardiovascular', 'systolicBP', bloodData.systolicBP), 15);
  check(bloodData.vitaminD, () => getStatus('vitamins', 'vitaminD', bloodData.vitaminD), 8);
  check(bloodData.crp, () => parseFloat(bloodData.crp) > 5 ? 'bad' : 'ok', 10);
  check(mental?.stressScore, () => parseFloat(mental.stressScore) < 40 ? 'ok' : parseFloat(mental.stressScore) < 65 ? 'warn' : 'bad', 12);

  const bmi = calcBMI(body?.height, body?.weight);
  if (bmi) {
    checks++;
    if (parseFloat(bmi) >= 30) penalties += 10;
    else if (parseFloat(bmi) >= 25) penalties += 5;
  }

  if (checks === 0) return null;
  return Math.max(0, Math.round(score - penalties));
}

function calcHealthScoreBreakdown(bloodData, body, mental, fitness) {
  const categories = [
    {
      label: 'Blood Glucose',
      icon: 'droplet',
      weight: 15,
      color: '#f05050',
      get: () => {
        if (!bloodData.fastingGlucose) return null;
        const s = getStatus('glucose', 'fastingGlucose', bloodData.fastingGlucose);
        const penalty = s === 'bad' ? 15 : s === 'warn' ? 7.5 : 0;
        return { value: bloodData.fastingGlucose + ' mmol/L', status: s, penalty, max: 15 };
      }
    },
    {
      label: 'Cholesterol',
      icon: 'heart',
      weight: 12,
      color: '#f0a050',
      get: () => {
        if (!bloodData.totalCholesterol) return null;
        const s = getStatus('lipids', 'totalCholesterol', bloodData.totalCholesterol);
        const penalty = s === 'bad' ? 12 : s === 'warn' ? 6 : 0;
        return { value: bloodData.totalCholesterol + ' mmol/L', status: s, penalty, max: 12 };
      }
    },
    {
      label: 'Blood Pressure',
      icon: 'heart',
      weight: 15,
      color: '#f05050',
      get: () => {
        if (!bloodData.systolicBP) return null;
        const s = getStatus('cardiovascular', 'systolicBP', bloodData.systolicBP);
        const penalty = s === 'bad' ? 15 : s === 'warn' ? 7.5 : 0;
        return { value: bloodData.systolicBP + (bloodData.diastolicBP ? '/' + bloodData.diastolicBP : '') + ' mmHg', status: s, penalty, max: 15 };
      }
    },
    {
      label: 'Vitamin D',
      icon: 'sun',
      weight: 8,
      color: '#f0a050',
      get: () => {
        if (!bloodData.vitaminD) return null;
        const s = getStatus('vitamins', 'vitaminD', bloodData.vitaminD);
        const penalty = s === 'bad' ? 8 : s === 'warn' ? 4 : 0;
        return { value: bloodData.vitaminD + ' nmol/L', status: s, penalty, max: 8 };
      }
    },
    {
      label: 'Inflammation (CRP)',
      icon: 'flame',
      weight: 10,
      color: '#f05050',
      get: () => {
        if (!bloodData.crp) return null;
        const v = parseFloat(bloodData.crp);
        const s = v > 5 ? 'bad' : v > 3 ? 'warn' : 'ok';
        const penalty = s === 'bad' ? 10 : s === 'warn' ? 5 : 0;
        return { value: bloodData.crp + ' mg/L', status: s, penalty, max: 10 };
      }
    },
    {
      label: 'Stress',
      icon: 'brain',
      weight: 12,
      color: '#a78bfa',
      get: () => {
        if (!mental?.stressScore) return null;
        const v = parseFloat(mental.stressScore);
        const s = v < 40 ? 'ok' : v < 65 ? 'warn' : 'bad';
        const penalty = s === 'bad' ? 12 : s === 'warn' ? 6 : 0;
        return { value: mental.stressScore + '/100', status: s, penalty, max: 12 };
      }
    },
    {
      label: 'BMI',
      icon: 'scale',
      weight: 10,
      color: '#f0a050',
      get: () => {
        const bmi = calcBMI(body?.height, body?.weight);
        if (!bmi) return null;
        const v = parseFloat(bmi);
        const s = v >= 30 ? 'bad' : v >= 25 ? 'warn' : 'ok';
        const penalty = v >= 30 ? 10 : v >= 25 ? 5 : 0;
        return { value: bmi + ' kg/m²', status: s, penalty, max: 10 };
      }
    }
  ];

  return categories.map(cat => ({ ...cat, result: cat.get() })).filter(cat => cat.result !== null);
}


function getScoreLabel(score) {
  if (score >= 85) return { label: 'Excellent', color: '#00c896' };
  if (score >= 70) return { label: 'Good', color: 'var(--primary)' };
  if (score >= 50) return { label: 'Fair', color: '#f0a050' };
  return { label: 'Needs attention', color: '#f05050' };
}

// ── Gamification ─────────────────────────────────────────────────────────────
function getBadges(recorded, totalMarkers, hasHistory, score) {
  const badges = [];
  if (recorded >= 1) badges.push({ icon: 'flask', label: 'First upload', desc: 'Uploaded your first health data' });
  if (recorded >= 10) badges.push({ icon: 'bar-chart', label: 'Data pioneer', desc: '10+ markers recorded' });
  if (recorded >= totalMarkers * 0.5) badges.push({ icon: 'star', label: 'Half way there', desc: '50% of all markers recorded' });
  if (recorded >= totalMarkers) badges.push({ icon: 'award', label: 'Complete profile', desc: 'All 36 markers recorded' });
  if (hasHistory) badges.push({ icon: 'trending-up', label: 'Trend tracker', desc: 'Recording data consistently' });
  if (score && score >= 85) badges.push({ icon: 'check-circle', label: 'Health champion', desc: 'Health score above 85' });
  return badges;
}

function getNextMilestone(recorded, totalMarkers) {
  if (recorded === 0) return { target: 1, label: 'Upload your first marker' };
  if (recorded < 5) return { target: 5, label: 'Record 5 markers', current: recorded };
  if (recorded < 10) return { target: 10, label: 'Record 10 markers', current: recorded };
  if (recorded < Math.round(totalMarkers * 0.5)) return { target: Math.round(totalMarkers * 0.5), label: 'Reach 50% coverage', current: recorded };
  if (recorded < totalMarkers) return { target: totalMarkers, label: 'Complete your profile', current: recorded };
  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function HealthScoreRing({ score }) {
  if (!score) return null;
  const { label, color } = getScoreLabel(score);
  const radius = 54;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="70" cy="70" r={radius} fill="none" stroke="var(--border2)" strokeWidth="10" />
        <circle cx="70" cy="70" r={radius} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }} />
      </svg>
      <div style={{ position: 'absolute', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '2rem', fontWeight: 500, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '0.2rem' }}>{label}</div>
      </div>
    </div>
  );
}

function MetricTile({ label, value, unit, status, trend, onTalkToRico }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.none;
  const dimColor = STATUS_DIM[status] || STATUS_DIM.none;
  const hasValue = value && value !== '—';
  const trendIcon = trend === 'up' ? '↑' : trend === 'down' ? '↓' : null;
  const trendColor = trend === 'up' ? 'var(--danger)' : trend === 'down' ? 'var(--success)' : 'var(--text3)';

  return (
    <div onClick={hasValue && status !== 'none' && onTalkToRico ? onTalkToRico : undefined}
      style={{
        background: 'var(--bg-card)',
        border: `1px solid ${hasValue && status !== 'none' ? (status === 'ok' ? 'rgba(0,200,150,.2)' : status === 'warn' ? 'rgba(240,160,80,.2)' : 'rgba(240,80,80,.2)') : 'var(--border2)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '1.25rem 1.5rem',
        position: 'relative',
        overflow: 'hidden',
        cursor: hasValue && status !== 'none' && onTalkToRico ? 'pointer' : 'default',
        transition: 'transform .15s, box-shadow .15s',
      }}
      onMouseEnter={e => { if (hasValue && status !== 'none') e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
    >
      {hasValue && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: color }} />
      )}
      <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.9rem', fontWeight: 600, color: hasValue ? color : 'var(--text3)', lineHeight: 1, letterSpacing: '-.02em' }}>
          {value || '—'}
        </div>
        {trendIcon && (
          <span style={{ fontSize: '1rem', color: trendColor, fontWeight: 700 }}>{trendIcon}</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '.3rem' }}>
        <span style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>{unit}</span>
        {hasValue && (
          <span style={{ fontSize: '.6rem', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', padding: '.15rem .45rem', borderRadius: '10px', background: dimColor, color }}>
            {STATUS_LABELS[status]}
          </span>
        )}
      </div>
      {hasValue && status !== 'none' && onTalkToRico && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.65rem', color: 'var(--primary)', opacity: 0.7 }}>
          Ask Rico about this →
        </div>
      )}
    </div>
  );
}

function InsightCard({ level, title, text, onAskRico }) {
  const colors = { warn: 'var(--warning)', bad: 'var(--danger)', ok: 'var(--success)', '': 'var(--primary)' };
  const dims = { warn: 'var(--warning-dim)', bad: 'var(--danger-dim)', ok: 'var(--success-dim)', '': 'var(--primary-dim)' };
  const icons = { warn: '↑', bad: '!', ok: '✓', '': '·' };
  const color = colors[level] || colors[''];
  const dim = dims[level] || dims[''];

  return (
    <div style={{
      display: 'flex', gap: '1rem', padding: '1rem 1.25rem',
      background: 'var(--bg-card)', border: '1px solid var(--border2)',
      borderLeft: `3px solid ${color}`, borderRadius: 'var(--radius-lg)',
      marginBottom: '.5rem', alignItems: 'flex-start'
    }}>
      <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: dim, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 700, flexShrink: 0, marginTop: '.1rem' }}>
        {icons[level] || icons['']}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '.8rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.3rem', lineHeight: 1.3 }}>{title}</div>
        <div style={{ fontSize: '.75rem', color: 'var(--text2)', lineHeight: 1.55, fontWeight: 300 }}>{text}</div>
        {onAskRico && (
          <button onClick={onAskRico} style={{
            marginTop: '0.6rem', padding: '.25rem .75rem', borderRadius: '20px',
            background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,.3)',
            color: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: '.68rem',
            fontWeight: 500, cursor: 'pointer'
          }}>
            Ask Rico for advice →
          </button>
        )}
      </div>
    </div>
  );
}

function BadgeCard({ icon, label, desc }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
      padding: '1rem', background: 'var(--bg-card)', border: '1px solid var(--border2)',
      borderRadius: 'var(--radius-lg)', textAlign: 'center', minWidth: 90
    }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} size={18} color="var(--primary)" />
      </div>
      <div style={{ fontSize: '.7rem', fontWeight: 500, color: 'var(--text)' }}>{label}</div>
      <div style={{ fontSize: '.62rem', color: 'var(--text3)', fontWeight: 300 }}>{desc}</div>
    </div>
  );
}

function generateInsights(bloodData, body, mental, fitness, profile) {
  const insights = [];
  const bmi = calcBMI(body?.height, body?.weight);

  if (bloodData.systolicBP && parseFloat(bloodData.systolicBP) >= 140) {
    insights.push({ level: 'bad', title: 'Blood pressure in hypertension range', text: 'Your reading of ' + bloodData.systolicBP + '/' + (bloodData.diastolicBP || '?') + ' mmHg is above the 140/90 threshold. Reducing salt, increasing aerobic activity and stress management are the evidence-based first interventions. Please consult your GP.' });
  } else if (bloodData.systolicBP && parseFloat(bloodData.systolicBP) >= 120) {
    insights.push({ level: 'warn', title: 'Blood pressure above optimal', text: 'Your reading of ' + bloodData.systolicBP + '/' + (bloodData.diastolicBP || '?') + ' mmHg is above the optimal 120/80 mmHg. Lifestyle changes can bring this down meaningfully.' });
  }
  if (bloodData.fastingGlucose && getStatus('glucose', 'fastingGlucose', bloodData.fastingGlucose) !== 'ok') {
    insights.push({ level: 'warn', title: 'Glucose above optimal range', text: 'Fasting glucose of ' + bloodData.fastingGlucose + ' mmol/L is above the 3.5–5.5 mmol/L optimal range. Reducing refined carbohydrates, increasing fibre and adding 30 minutes of daily walking are the most effective interventions.' });
  }
  if (bloodData.hba1c && getStatus('glucose', 'hba1c', bloodData.hba1c) !== 'ok') {
    insights.push({ level: 'warn', title: 'HbA1c indicates pre-diabetic range', text: 'HbA1c of ' + bloodData.hba1c + '% reflects average blood sugar over 3 months. Dietary changes and regular exercise can bring this down significantly within 3–6 months.' });
  }
  if (bloodData.totalCholesterol && getStatus('lipids', 'totalCholesterol', bloodData.totalCholesterol) !== 'ok') {
    insights.push({ level: 'warn', title: 'Total cholesterol elevated', text: 'Total cholesterol of ' + bloodData.totalCholesterol + ' mmol/L is above the 5.0 mmol/L optimal. Oily fish 2–3x per week, reducing saturated fat and regular aerobic exercise are the evidence-based first steps.' });
  }
  if (bloodData.vitaminD && getStatus('vitamins', 'vitaminD', bloodData.vitaminD) !== 'ok') {
    insights.push({ level: 'warn', title: 'Vitamin D is below optimal', text: 'Vitamin D of ' + bloodData.vitaminD + ' nmol/L is below the optimal 50 nmol/L. Daily supplementation of 1,000–2,000 IU is typically recommended, especially in the UK.' });
  }
  if (bloodData.crp && parseFloat(bloodData.crp) > 5) {
    insights.push({ level: 'warn', title: 'Elevated inflammation marker (CRP)', text: 'CRP of ' + bloodData.crp + ' mg/L suggests elevated systemic inflammation. Anti-inflammatory foods, stress reduction and quality sleep are the primary lifestyle interventions.' });
  }
  if (mental?.stressScore && parseFloat(mental.stressScore) > 60) {
    insights.push({ level: 'warn', title: 'Stress levels are elevated', text: 'Stress score of ' + mental.stressScore + '/100 warrants attention. Chronic stress elevates cortisol, directly impacting glucose, cholesterol and immune function.' });
  }
  if (bmi && parseFloat(bmi) >= 25) {
    insights.push({ level: 'warn', title: 'BMI above healthy range', text: 'BMI of ' + bmi + ' is in the ' + (parseFloat(bmi) >= 30 ? 'obese' : 'overweight') + ' range. Even a 5–7% reduction in body weight can significantly improve metabolic markers.' });
  }
  if (bloodData.totalCholesterol && getStatus('lipids', 'totalCholesterol', bloodData.totalCholesterol) === 'ok' && bloodData.hdl && getStatus('lipids', 'hdl', bloodData.hdl) === 'ok') {
    insights.push({ level: 'ok', title: 'Cholesterol profile is healthy', text: 'Total cholesterol ' + bloodData.totalCholesterol + ' mmol/L and HDL ' + bloodData.hdl + ' mmol/L are both within optimal ranges. Keep up your current dietary and exercise habits.' });
  }
  if (insights.length === 0) {
    insights.push({ level: 'ok', title: 'Your recorded markers look good', text: 'Based on your current data, health markers are within healthy ranges. Record data regularly to track trends over time.' });
  }
  return insights.slice(0, 5);
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Dashboard({ onTabChange }) {
  const [loading, setLoading] = useState(true);
  const [bloodData, setBloodData] = useState({});
  const [body, setBody] = useState({});
  const [mental, setMental] = useState({});
  const [fitness, setFitness] = useState({});
  const [profile, setProfile] = useState({});
  const [glucoseHistory, setGlucoseHistory] = useState([]);
  const [nutritionProfile, setNutritionProfile] = useState(null);
  const [todayFoodLog, setTodayFoodLog] = useState([]);
  const [trainingProfile, setTrainingProfile] = useState(null);
  const [lastSession, setLastSession] = useState(null);
  const [trainingGam, setTrainingGam] = useState(null);
  const [nextSession, setNextSession] = useState(null);

  const user = auth.currentUser;
  const userName = profile.name?.split(' ')[0] || profile.firstName || user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'there';
  const age = calcAge(profile.dob);
  const bmi = calcBMI(body.height, body.weight);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [all, bo, m, f, p, gh, np, tp, gam] = await Promise.all([
          loadAllBloodMarkers(), loadLatestMetrics('body'), loadLatestMetrics('mental'),
          loadLatestMetrics('fitness'), loadLatestMetrics('profile'), loadMetricHistory('blood', 6),
          loadLatestMetrics('nutritionProfile'), loadLatestMetrics('trainingProfile'),
          loadLatestMetrics('gamification')
        ]);
        if (all) setBloodData(all);
        if (bo) setBody(bo);
        if (m) setMental(m);
        if (f) setFitness(f);
        if (p) setProfile(p);
        if (gh) setGlucoseHistory(gh);
        if (np) setNutritionProfile(np);
        if (tp) setTrainingProfile(tp);
        if (gam) setTrainingGam(gam);

        // Load today's food log
        const user = auth.currentUser;
        if (user) {
          try {
            const today = new Date().toISOString().split('T')[0];
            const q = query(collection(db, 'users', user.uid, 'history', 'nutrition', 'entries'), orderBy('recordedAt', 'desc'), limit(10));
            const snap = await getDocs(q);
            const todayEntries = snap.docs.map(d => d.data()).filter(e => e.date === today);
            setTodayFoodLog(todayEntries);

            // Load last training session
            const sessQ = query(collection(db, 'users', user.uid, 'history', 'training', 'entries'), orderBy('recordedAt', 'desc'), limit(1));
            const sessSnap = await getDocs(sessQ);
            if (!sessSnap.empty) setLastSession(sessSnap.docs[0].data());

            // Load programme to find next session
            try {
              const progSnap = await getDoc(doc(db, 'users', user.uid, 'latest', 'programme'));
              const tpSnap = await getDoc(doc(db, 'users', user.uid, 'latest', 'trainingProfile'));
              if (progSnap.exists() && tpSnap.exists()) {
                const prog = progSnap.data();
                const tp = tpSnap.data();
                const currentWeek = tp.currentWeek || 1;
                const weeks = prog.weeks || [];
                const weekData = weeks.find(w => w.week === currentWeek) || weeks[0];
                if (weekData && weekData.sessions) {
                  // Find first session not yet done today
                  const todayDate = new Date().toISOString().split('T')[0];
                  const recentDates = sessSnap.docs.map(d => d.data().date);
                  const nextSess = weekData.sessions.find(s => !recentDates.includes(todayDate) || true);
                  if (nextSess) setNextSession({ ...nextSess, week: currentWeek });
                }
              }
            } catch(e) { console.log('Next session load error:', e.message); }
          } catch(e) { console.log('Dashboard extra data error:', e.message); }
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  const hasData = Object.keys(bloodData).some(k => bloodData[k]) || body.weight || mental.stressScore;
  const recorded = Object.keys(bloodData).filter(k => bloodData[k]).length;
  const totalMarkers = Object.values(BIOMARKER_GROUPS).reduce((s, g) => s + Object.keys(g.markers).length, 0);
  const flagged = Object.entries(BIOMARKER_GROUPS).reduce((sum, [gk, g]) =>
    sum + Object.keys(g.markers).filter(mk => { const s = getStatus(gk, mk, bloodData[mk]); return s === 'warn' || s === 'bad'; }).length, 0);

  const healthScore = calcHealthScore(bloodData, body, mental, fitness);
  const badges = getBadges(recorded, totalMarkers, glucoseHistory.length > 1, healthScore);
  const milestone = getNextMilestone(recorded, totalMarkers);
  const milestoneProgress = milestone?.current ? Math.round((milestone.current / milestone.target) * 100) : recorded >= 1 ? 100 : 0;

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: 'var(--bg-card)', titleColor: 'var(--text)', bodyColor: 'var(--text3)', borderColor: 'var(--border2)', borderWidth: 1 } },
    scales: {
      x: { grid: { color: 'rgba(128,128,128,.1)', drawBorder: false }, ticks: { color: '#888', font: { size: 10, family: 'DM Sans' } } },
      y: { grid: { color: 'rgba(128,128,128,.1)', drawBorder: false }, ticks: { color: '#888', font: { size: 10, family: 'DM Sans' } }, min: 3, max: 9 }
    }
  };

  const handleTalkToRico = (topic) => {
    if (onTabChange) onTabChange('advisor');
  };

  if (loading) return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ width: '200px', height: '28px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }} />
      <div style={{ width: '300px', height: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 0 3rem' }}>
      <style>{`
        @media (max-width: 768px) {
          .dash-actions { grid-template-columns: 1fr 1fr !important; }
          .dash-score-grid { grid-template-columns: 1fr !important; }
          .dash-metrics-grid { grid-template-columns: 1fr 1fr !important; }
          .dash-progress-grid { grid-template-columns: 1fr !important; }
          .dash-trend-grid { grid-template-columns: 1fr !important; }
          .dash-flagged { display: none !important; }
        }
        @media (max-width: 480px) {
          .dash-actions { grid-template-columns: 1fr !important; }
          .dash-metrics-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.75rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.25rem' }}>
            {getGreeting()}, {userName}
          </h1>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>
            {age && profile.gender ? age + ' · ' + profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) + ' · ' : ''}
            {recorded} of {totalMarkers} markers recorded
            {flagged > 0 ? ' · ' + flagged + ' flagged' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {flagged > 0 && (
            <div style={{ padding: '.4rem 1rem', borderRadius: '20px', background: 'var(--warning-dim)', border: '1px solid rgba(240,160,80,.2)', fontSize: '.7rem', fontWeight: 600, color: 'var(--warning)', letterSpacing: '.05em', textTransform: 'uppercase' }}>
              {flagged} marker{flagged !== 1 ? 's' : ''} flagged
            </div>
          )}
        </div>
      </div>

      {/* ── WEEK AT A GLANCE ── */}
      {(nutritionProfile || trainingProfile || lastSession) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '.85rem', marginBottom: '1.5rem' }}>

          {/* Nutrition summary */}
          {nutritionProfile?.targetCalories && (
            <div onClick={() => onTabChange && onTabChange('nutrition')} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', cursor: 'pointer', transition: 'border-color .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#f0a050'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: '#f0a050', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}><Icon name="apple" size={14} /> Today's Nutrition</div>
              {(() => {
                const cals = Math.round(todayFoodLog.reduce((s, e) => s + (parseFloat(e.calories)||0), 0));
                const prot = Math.round(todayFoodLog.reduce((s, e) => s + (parseFloat(e.protein)||0), 0));
                const target = nutritionProfile.targetCalories;
                const pct = Math.min(100, Math.round((cals / target) * 100));
                return todayFoodLog.length > 0 ? (
                  <>
                    <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.5rem', fontWeight: 500, color: '#f0a050', marginBottom: '.2rem' }}>{cals} <span style={{ fontSize: '.75rem', color: 'var(--text3)' }}>/ {target} kcal</span></div>
                    <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, marginBottom: '.3rem' }}>
                      <div style={{ width: pct + '%', height: '100%', background: '#f0a050', borderRadius: 2, transition: 'width .6s ease' }} />
                    </div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{prot}g protein · {todayFoodLog.length} entr{todayFoodLog.length === 1 ? 'y' : 'ies'} logged</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '.88rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '.3rem' }}>Nothing logged yet today</div>
                    <div style={{ fontSize: '.72rem', color: '#f0a050' }}>Target: {target} kcal · {nutritionProfile.proteinTarget}g protein</div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Training summary */}
          {trainingProfile && (
            <div onClick={() => onTabChange && onTabChange('training')} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', cursor: 'pointer', transition: 'border-color .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--success)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}><Icon name="dumbbell" size={14} /> Training</div>
              {lastSession ? (
                <>
                  <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.1rem', fontWeight: 500, color: 'var(--success)', marginBottom: '.2rem' }}>{lastSession.sessionName || 'Session logged'}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>Last: {lastSession.date || 'recently'}</div>
                  {nextSession && <div style={{ fontSize: '.72rem', color: 'var(--primary)', marginTop: '.2rem' }}>Next: {nextSession.sessionName} — Week {nextSession.week}</div>}
                  {trainingGam?.streak > 1 && <div style={{ fontSize: '.72rem', color: '#f0a050', marginTop: '.2rem' }}>{trainingGam.streak}<Icon name="flame" size={14} /> day streak · {trainingGam.xp || 0} XP</div>}
                </>
              ) : (
                <>
                  <div style={{ fontSize: '.88rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '.2rem' }}>{trainingProfile.goal || 'Programme active'}</div>
                  {nextSession && <div style={{ fontSize: '.72rem', color: 'var(--primary)' }}>Next: {nextSession.sessionName} — Week {nextSession.week}</div>}
                  {!nextSession && <div style={{ fontSize: '.72rem', color: 'var(--success)' }}>Log your first session →</div>}
                </>
              )}
            </div>
          )}

          {/* Blood marker highlight */}
          {bloodData && Object.keys(bloodData).some(k => bloodData[k]) && (
            <div onClick={() => onTabChange && onTabChange('health')} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', cursor: 'pointer', transition: 'border-color .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}><Icon name="flask" size={14} /> Health Score</div>
              <div style={{ fontFamily: 'var(--font-head)', fontSize: '2rem', fontWeight: 600, color: healthScore >= 80 ? 'var(--success)' : healthScore >= 60 ? 'var(--warning)' : 'var(--danger)', lineHeight: 1, marginBottom: '.2rem' }}>{healthScore || '—'}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{recorded} markers recorded · {flagged} flag{flagged !== 1 ? 's' : ''}</div>
            </div>
          )}

        </div>
      )}

      {/* ── QUICK ACTIONS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { icon: 'message', label: 'Talk to Rico', desc: 'Get personalised advice', tab: 'advisor', color: 'var(--primary)' },
          { icon: 'upload', label: 'Upload Data', desc: 'Add blood test results', tab: 'upload', color: '#00c896' },
          { icon: 'activity', label: 'My Metrics', desc: 'View all biomarkers', tab: 'metrics', color: '#f0a050' },
          { icon: 'dumbbell', label: 'My Training', desc: 'View your programme', tab: 'training', color: '#a78bfa' },
        ].map(action => (
          <button key={action.tab} onClick={() => onTabChange && onTabChange(action.tab)} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem',
            cursor: 'pointer', textAlign: 'left', transition: 'transform .15s, border-color .15s',
            display: 'flex', alignItems: 'center', gap: '0.75rem'
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = action.color + '66'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = ''; }}
          >
            <div style={{ width: 36, height: 36, borderRadius: 8, background: action.color + '18', border: '1px solid ' + action.color + '33', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1rem', color: action.color, fontWeight: 700 }}><Icon name={action.icon} size={16} /></div>
            <div>
              <div style={{ fontSize: '.82rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.15rem' }}>{action.label}</div>
              <div style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>{action.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* ── DAILY TIP FROM RICO ── */}
      {(() => {
        const tips = [];
        if (!hasData) {
          tips.push({ icon: 'flask', tip: 'Upload your most recent blood test results to unlock your personalised health score and get specific advice from Rico on what your numbers mean.', prompt: 'What should I upload first to get started?' });
        } else if (bloodData.fastingGlucose && getStatus('glucose','fastingGlucose',bloodData.fastingGlucose) !== 'ok') {
          tips.push({ icon: 'utensils', tip: 'Your fasting glucose of ' + bloodData.fastingGlucose + ' mmol/L is above optimal. Reducing refined carbohydrates, eating more fibre and a 30-minute walk after meals can make a meaningful difference within weeks.', prompt: 'What can I do to improve my fasting glucose?' });
        } else if (bloodData.totalCholesterol && getStatus('lipids','totalCholesterol',bloodData.totalCholesterol) !== 'ok') {
          tips.push({ icon: '🫀', tip: 'Your cholesterol of ' + bloodData.totalCholesterol + ' mmol/L is above the 5.0 mmol/L optimal. Oily fish, less saturated fat and regular aerobic exercise are the most evidence-backed steps.', prompt: 'How can I lower my cholesterol naturally?' });
        } else if (mental.stressScore && parseFloat(mental.stressScore) > 60) {
          tips.push({ icon: 'brain', tip: 'Your stress score of ' + mental.stressScore + '/100 is elevated. Chronic stress raises cortisol which directly impacts your glucose, cholesterol and sleep quality — all measurable in your blood results.', prompt: 'How is stress affecting my health markers?' });
        } else if (bloodData.vitaminD && getStatus('vitamins','vitaminD',bloodData.vitaminD) !== 'ok') {
          tips.push({ icon: 'sun', tip: 'Your Vitamin D of ' + bloodData.vitaminD + ' nmol/L is below optimal. This affects energy, immunity and mood. 1,000–2,000 IU daily supplementation is typically recommended in the UK.', prompt: 'What are the effects of low Vitamin D and how do I fix it?' });
        } else {
          tips.push({ icon: 'check-circle', tip: 'Your recorded markers are looking healthy. The best thing you can do now is keep tracking — aim to update your blood results every 3–6 months to spot trends early.', prompt: 'What should I focus on to maintain my health score?' });
        }
        const { icon, tip, prompt } = tips[0];
        return (
          <div style={{ background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={icon} size={18} color="var(--primary)" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.3rem' }}>Rico's tip for you today</div>
              <div style={{ fontSize: '.82rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.6 }}>{tip}</div>
            </div>
            <button onClick={() => onTabChange && onTabChange('advisor')} style={{
              flexShrink: 0, padding: '.5rem 1.2rem', borderRadius: '20px',
              background: 'var(--primary)', border: 'none',
              color: '#ffffff', fontFamily: 'var(--font-body)', fontSize: '.75rem',
              fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
            }}>Ask Rico →</button>
          </div>
        );
      })()}

      {/* ── HEALTH SCORE + METRICS ── */}
      <div style={{ marginBottom: '1rem' }}>

        {/* Health Score Ring + Breakdown */}
        {hasData && healthScore && (() => {
          const breakdown = calcHealthScoreBreakdown(bloodData, body, mental, fitness);
          return (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>

                {/* Left — ring + summary */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.75rem', flexShrink: 0 }}>
                  <HealthScoreRing score={healthScore} />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.2rem' }}>Health Score</div>
                    <div style={{ fontSize: '.7rem', color: 'var(--text3)', fontWeight: 300 }}>{recorded}/{totalMarkers} markers</div>
                    {flagged > 0 && <div style={{ fontSize: '.7rem', color: 'var(--warning)', marginTop: '.1rem' }}>{flagged} flagged</div>}
                  </div>
                  {/* Legend */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                    {[['var(--success)', 'Optimal'], ['var(--warning)', 'Borderline'], ['var(--danger)', 'Elevated']].map(([color, label]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: '.65rem', color: 'var(--text3)', fontWeight: 300 }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right — category grid */}
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '.75rem', minWidth: 0 }}>
                  {breakdown.map((cat, i) => {
                    const { result } = cat;
                    const pct = Math.max(0, Math.round(((cat.weight - result.penalty) / cat.weight) * 100));
                    const barColor = result.status === 'ok' ? 'var(--success)' : result.status === 'warn' ? 'var(--warning)' : 'var(--danger)';
                    const statusLabel = result.status === 'ok' ? 'Optimal' : result.status === 'warn' ? 'Borderline' : 'Elevated';
                    return (
                      <div key={i} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '.85rem 1rem', border: '1px solid var(--border2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.6rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                            <Icon name={cat.icon} size={14} color={barColor} />
                            <span style={{ fontSize: '.78rem', fontWeight: 500, color: 'var(--text)' }}>{cat.label}</span>
                          </div>
                          <span style={{ fontSize: '.65rem', fontWeight: 600, color: barColor, padding: '.15rem .5rem', borderRadius: 20, background: barColor + '18' }}>{statusLabel}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.4rem' }}>
                          <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text2)' }}>{result.value}</span>
                        </div>
                        <div style={{ height: 4, background: 'var(--bg-card)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: pct + '%', height: '100%', background: barColor, borderRadius: 2, transition: 'width .8s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Metrics grid — 3 columns, uniform */}
        <div className="dash-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '.75rem' }}>
          <MetricTile label="Fasting Glucose" value={bloodData.fastingGlucose} unit="mmol/L" status={getStatus('glucose', 'fastingGlucose', bloodData.fastingGlucose)} onTalkToRico={() => handleTalkToRico('glucose')} />
          <MetricTile label="HbA1c" value={bloodData.hba1c} unit="%" status={getStatus('glucose', 'hba1c', bloodData.hba1c)} onTalkToRico={() => handleTalkToRico('hba1c')} />
          <MetricTile label="Total Cholesterol" value={bloodData.totalCholesterol} unit="mmol/L" status={getStatus('lipids', 'totalCholesterol', bloodData.totalCholesterol)} onTalkToRico={() => handleTalkToRico('cholesterol')} />
          <MetricTile label="Blood Pressure" value={bloodData.systolicBP && bloodData.diastolicBP ? bloodData.systolicBP + '/' + bloodData.diastolicBP : bloodData.systolicBP} unit="mmHg" status={getStatus('cardiovascular', 'systolicBP', bloodData.systolicBP)} onTalkToRico={() => handleTalkToRico('blood pressure')} />
          <MetricTile label="Vitamin D" value={bloodData.vitaminD} unit="nmol/L" status={getStatus('vitamins', 'vitaminD', bloodData.vitaminD)} onTalkToRico={() => handleTalkToRico('vitamin D')} />
          <MetricTile label="Stress Score" value={mental.stressScore} unit="/100" status={mental.stressScore ? (parseFloat(mental.stressScore) < 40 ? 'ok' : parseFloat(mental.stressScore) < 65 ? 'warn' : 'bad') : 'none'} onTalkToRico={() => handleTalkToRico('stress')} />
          <MetricTile label="BMI" value={bmi} unit="kg/m²" status={bmi ? (parseFloat(bmi) < 25 ? 'ok' : parseFloat(bmi) < 30 ? 'warn' : 'bad') : 'none'} />
          <MetricTile label="LDL Cholesterol" value={bloodData.ldl} unit="mmol/L" status={getStatus('lipids', 'ldl', bloodData.ldl)} onTalkToRico={() => handleTalkToRico('ldl')} />
          <MetricTile label="Ferritin" value={bloodData.ferritin} unit="ug/L" status={getStatus('vitamins', 'ferritin', bloodData.ferritin)} onTalkToRico={() => handleTalkToRico('ferritin')} />
        </div>
      </div>

      {/* ── PROGRESS + BADGES ── */}
      <div className="dash-progress-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

        {/* Milestone progress */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Your Progress</div>
          {milestone ? (
            <>
              <div style={{ fontSize: '.82rem', color: 'var(--text)', marginBottom: '0.75rem', fontWeight: 400 }}>{milestone.label}</div>
              <div style={{ height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', marginBottom: '0.5rem' }}>
                <div style={{ height: '100%', width: milestoneProgress + '%', background: 'var(--primary)', borderRadius: '3px', transition: 'width 1s ease' }} />
              </div>
              <div style={{ fontSize: '.68rem', color: 'var(--text3)' }}>
                {milestone.current || (recorded >= 1 ? 1 : 0)} / {milestone.target} markers
              </div>
            </>
          ) : (
            <div style={{ fontSize: '.82rem', color: 'var(--success)' }}><Icon name="award" size={14} /> Profile complete! All markers recorded.</div>
          )}
          {!hasData && (
            <button onClick={() => onTabChange && onTabChange('upload')} style={{
              marginTop: '1rem', padding: '.5rem 1rem', borderRadius: '20px',
              background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,.3)',
              color: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: '.75rem',
              fontWeight: 500, cursor: 'pointer'
            }}>Upload your first blood test →</button>
          )}
        </div>

        {/* Badges */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Achievements</div>
          {badges.length > 0 ? (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {badges.map((b, i) => <BadgeCard key={i} {...b} />)}
            </div>
          ) : (
            <div style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>
              Upload your first health data to earn your first badge.
            </div>
          )}
        </div>
      </div>

      {/* ── MENTAL HEALTH ASSESSMENTS ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.75rem' }}>
          <div>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.2rem' }}>Mental Health Assessments</div>
            <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300 }}>Clinically validated screening tools — PHQ-9, GAD-7, Epworth</div>
          </div>
          <button onClick={() => onTabChange && onTabChange('assessments')} style={{ padding: '.45rem 1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: '.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.4rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <Icon name="clipboard" size={13} color="var(--primary)" />
            Take Assessments
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
          {[
            { key: 'phq9', label: 'PHQ-9', sub: 'Depression', max: 27, fn: (s) => s < 5 ? { label: 'Minimal', color: 'var(--success)' } : s < 10 ? { label: 'Mild', color: 'var(--warning)' } : s < 15 ? { label: 'Moderate', color: 'var(--warning)' } : { label: 'Severe', color: 'var(--danger)' } },
            { key: 'gad7', label: 'GAD-7', sub: 'Anxiety', max: 21, fn: (s) => s < 5 ? { label: 'Minimal', color: 'var(--success)' } : s < 10 ? { label: 'Mild', color: 'var(--warning)' } : s < 15 ? { label: 'Moderate', color: 'var(--warning)' } : { label: 'Severe', color: 'var(--danger)' } },
            { key: 'epworth', label: 'Epworth', sub: 'Sleepiness', max: 24, fn: (s) => s < 8 ? { label: 'Normal', color: 'var(--success)' } : s < 11 ? { label: 'Mild', color: 'var(--warning)' } : s < 16 ? { label: 'Moderate', color: 'var(--warning)' } : { label: 'Severe', color: 'var(--danger)' } },
          ].map(({ key, label, sub, max, fn }) => {
            const score = mental[key];
            const hasScore = score !== undefined && score !== null;
            const sev = hasScore ? fn(parseFloat(score)) : null;
            return (
              <div key={key} onClick={() => onTabChange && onTabChange('assessments')} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '1rem', cursor: 'pointer', border: '1px solid transparent', transition: 'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.5rem' }}>
                  <div>
                    <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text)' }}>{label}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>{sub}</div>
                  </div>
                  {hasScore
                    ? <div style={{ padding: '.2rem .6rem', borderRadius: 20, background: sev.color + '18', border: '1px solid ' + sev.color + '30' }}><span style={{ fontSize: '.65rem', fontWeight: 600, color: sev.color }}>{sev.label}</span></div>
                    : <div style={{ padding: '.2rem .6rem', borderRadius: 20, background: 'var(--bg-card)', border: '1px solid var(--border2)' }}><span style={{ fontSize: '.65rem', color: 'var(--text3)' }}>Not taken</span></div>
                  }
                </div>
                {hasScore
                  ? <div style={{ display: 'flex', alignItems: 'baseline', gap: '.3rem' }}>
                      <span style={{ fontSize: '1.4rem', fontWeight: 700, color: sev.color, lineHeight: 1 }}>{score}</span>
                      <span style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>/ {max}</span>
                    </div>
                  : <div style={{ fontSize: '.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                      <Icon name="arrow-right" size={12} color="var(--primary)" /> Take now
                    </div>
                }
              </div>
            );
          })}
        </div>
      </div>

      {/* ── GLUCOSE TREND + BIOMARKER COVERAGE ── */}
      <div className="dash-trend-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Glucose Trend</div>
          {glucoseHistory.length > 1 ? (
            <div style={{ height: '140px' }}>
              <Line data={{
                labels: glucoseHistory.map(e => formatDate(e.recordedAt)),
                datasets: [
                  { data: glucoseHistory.map(e => parseFloat(e.fastingGlucose) || null), borderColor: '#f0a050', backgroundColor: 'rgba(240,160,80,.06)', tension: .5, fill: true, pointRadius: 4, pointBackgroundColor: '#f0a050', borderWidth: 2 },
                  { data: glucoseHistory.map(() => 5.5), borderColor: 'rgba(240,80,80,.3)', borderDash: [4, 4], pointRadius: 0, borderWidth: 1 }
                ]
              }} options={chartOpts} />
            </div>
          ) : (
            <div style={{ height: '140px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '.5rem' }}>
              <div style={{ fontSize: '1.5rem', opacity: .3 }}>〜</div>
              <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, textAlign: 'center' }}>Record 2+ glucose readings<br />to see your trend</div>
            </div>
          )}
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Biomarker Coverage</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.55rem' }}>
            {Object.entries(BIOMARKER_GROUPS).map(([gk, group]) => {
              const markers = Object.keys(group.markers);
              const rec = markers.filter(mk => bloodData[mk]);
              const flaggedInGroup = markers.filter(mk => { const s = getStatus(gk, mk, bloodData[mk]); return s === 'warn' || s === 'bad'; });
              const pct = markers.length > 0 ? Math.round(rec.length / markers.length * 100) : 0;
              return (
                <div key={gk} style={{ display: 'flex', alignItems: 'center', gap: '.85rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: group.color, flexShrink: 0 }} />
                  <div style={{ fontSize: '.72rem', color: 'var(--text2)', width: '140px', flexShrink: 0, fontWeight: 300 }}>{group.label}</div>
                  <div style={{ flex: 1, height: '4px', background: 'var(--bg-elevated)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: pct + '%', height: '100%', background: flaggedInGroup.length > 0 ? 'var(--warning)' : rec.length > 0 ? 'var(--success)' : 'transparent', borderRadius: '2px', transition: 'width .6s cubic-bezier(.4,0,.2,1)' }} />
                  </div>
                  <div style={{ fontSize: '.65rem', color: 'var(--text3)', width: '45px', textAlign: 'right', flexShrink: 0 }}>
                    {rec.length}/{markers.length}
                    {flaggedInGroup.length > 0 && <span style={{ color: 'var(--warning)', marginLeft: '.3rem' }}>↑</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── RICO'S INSIGHTS ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Rico's Insights</div>
          <button onClick={() => onTabChange && onTabChange('advisor')} style={{
            padding: '.3rem .85rem', borderRadius: '20px',
            background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,.3)',
            color: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: '.72rem',
            fontWeight: 500, cursor: 'pointer'
          }}>Open AI Advisor →</button>
        </div>
        {hasData ? generateInsights(bloodData, body, mental, fitness, profile).map((ins, i) => (
          <InsightCard key={i} level={ins.level} title={ins.title} text={ins.text}
            onAskRico={ins.level !== 'ok' ? () => handleTalkToRico(ins.title) : null} />
        )) : (
          <div style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, padding: '.5rem 0' }}>
            Rico's insights will appear here once you've entered your health metrics in Upload Data.
          </div>
        )}
        {bloodData.updatedAt && (
          <div style={{ marginTop: '.75rem', fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>
            Last updated: {formatDate(bloodData.updatedAt)}
          </div>
        )}
      </div>



    </div>
  );
}
